import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  FailureNotice,
  type HeatStrings,
  heatMessages,
} from "@/components/tools/chem-physical/heat-shared";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  HEAT_MASS_UNITS,
  unitOptionsOf,
} from "@/lib/tool-kit/chem-physical/heat";
import {
  type CurveSegment,
  type CurveSolution,
  type WaterPhase,
  waterHeatingCurve,
} from "@/lib/tool-kit/chem-physical/heat-modes";
import { TEMPERATURE_UNITS } from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

const DEFAULT_SIG = 4;
const MIN_SIG = 4;
const PHASES: WaterPhase[] = ["ice", "water", "steam"];
const TEMPERATURE_OPTIONS = TEMPERATURE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));

function stepRow(
  seg: CurveSegment,
  grams: number,
  sig: number,
  strings: HeatStrings,
  fmt: ReturnType<typeof useToolNumber>
): string[] {
  const p = (value: number) => formatPlain(fmt, value);
  const names: Record<WaterPhase, string> = {
    ice: strings.phaseIce,
    water: strings.phaseWater,
    steam: strings.phaseSteam,
  };
  const heat = formatSig(fmt, seg.q / 1000, sig);
  if (seg.kind === "warm") {
    const change = seg.to - seg.from;
    return [
      t(strings.stepWarm, {
        phase: names[seg.phase],
        from: p(seg.from),
        to: p(seg.to),
      }),
      t(strings.calcWarm, { m: p(grams), c: p(seg.rate), dt: p(change) }),
      heat,
    ];
  }
  const labels = {
    melt: strings.stepMelt,
    freeze: strings.stepFreeze,
    boil: strings.stepBoil,
    condense: strings.stepCondense,
  };
  return [
    t(labels[seg.kind], { temperature: p(seg.from) }),
    t(strings.calcLatent, { m: p(grams), l: p(seg.rate) }),
    heat,
  ];
}

function CurveAnswer({
  solution,
  sig,
  strings,
}: {
  solution: CurveSolution;
  sig: number;
  strings: HeatStrings;
}) {
  const fmt = useToolNumber();
  const total = `${formatSig(fmt, solution.total / 1000, sig)} kJ`;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.curveTotal} value={total} />
        <StatCard
          label={strings.direction}
          value={solution.total >= 0 ? strings.absorbed : strings.released}
        />
        <StatCard
          label={strings.curveSteps}
          value={formatPlain(fmt, solution.segments.length)}
        />
      </StatGrid>
      <DataTable
        head={[
          strings.columnStep,
          strings.columnCalculation,
          strings.columnHeat,
        ]}
        rows={solution.segments.map((seg) =>
          stepRow(seg, solution.grams, sig, strings, fmt)
        )}
      />
      <Notice>{strings.curveNote}</Notice>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, { label: strings.curveTotal, value: total })}
      />
    </>
  );
}

export function HeatCurvePanel({
  strings,
  sigChoice,
}: {
  strings: HeatStrings;
  sigChoice: string;
}) {
  const [mass, setMass] = useState("135");
  const [massUnit, setMassUnit] = useState("g");
  const [tempUnit, setTempUnit] = useState("C");
  const [start, setStart] = useState({
    text: "-15",
    phase: "ice" as WaterPhase,
  });
  const [end, setEnd] = useState({ text: "120", phase: "steam" as WaterPhase });
  const outcome = waterHeatingCurve({ mass, massUnit, start, end, tempUnit });
  const fewest = fewestSigFigs([mass, start.text, end.text], DEFAULT_SIG);
  const sig =
    sigChoice === "match" ? Math.max(fewest, MIN_SIG) : Number(sigChoice);
  const phaseNames: Record<WaterPhase, string> = {
    ice: strings.phaseIceTitle,
    water: strings.phaseWaterTitle,
    steam: strings.phaseSteamTitle,
  };
  const phaseOptions = PHASES.map((id) => ({
    value: id,
    label: phaseNames[id],
  }));
  const labels = {
    mass: strings.termM,
    start: strings.curveStartTemperature,
    end: strings.curveEndTemperature,
  };
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityInput
          id="shc-mass"
          label={strings.termM}
          onUnitChange={setMassUnit}
          onValueChange={setMass}
          unit={massUnit}
          unitLabel={strings.unitMass}
          units={unitOptionsOf(HEAT_MASS_UNITS)}
          value={mass}
        />
        <SelectField
          id="shc-temp-unit"
          label={strings.unitTemperature}
          onChange={setTempUnit}
          options={TEMPERATURE_OPTIONS}
          value={tempUnit}
        />
        <TextField
          id="shc-start"
          label={strings.curveStartTemperature}
          numeric
          onChange={(text) => setStart({ ...start, text })}
          value={start.text}
        />
        <SelectField
          id="shc-start-phase"
          label={strings.curveStartPhase}
          onChange={(phase) => setStart({ ...start, phase })}
          options={phaseOptions}
          value={start.phase}
        />
        <TextField
          id="shc-end"
          label={strings.curveEndTemperature}
          numeric
          onChange={(text) => setEnd({ ...end, text })}
          value={end.text}
        />
        <SelectField
          id="shc-end-phase"
          label={strings.curveEndPhase}
          onChange={(phase) => setEnd({ ...end, phase })}
          options={phaseOptions}
          value={end.phase}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <CurveAnswer sig={sig} solution={outcome} strings={strings} />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={heatMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
